import { LoadingOutlined } from "@ant-design/icons";
import { Col, Row, message } from "antd";
import type { RcFile } from "antd/es/upload/interface";
import { TFunction } from "i18next";
import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import EditIcon from "../../../assets/icons/edit-pen.svg";
import EditCover1 from "../../../assets/images/edit-cover-1.webp";
import EditCover2 from "../../../assets/images/edit-cover-2.webp";
import { IUserInfos } from "../../../helpers/types";
import { AppDispatch } from "../../../redux/store";
import { updateToDefaultAvatar } from "../../../redux/users/usersSlice";
import { userService } from "../../../services";
import { convertHeicFile, isImageAcceptedFormat } from "../../../utls/FunctionsUtil";
import AvatarOptionsModal from "../../../components/AvatarModals/AvatarOptionsModal";
import ConfirmDeleteAvatarModal from "../../../components/AvatarModals/ConfirmDeleteAvatarModal";

interface IProps {
  user: IUserInfos | undefined;
  t: TFunction<"translation", undefined, "translation">;
}
export default function EditCreatorAvatar({ user, t }: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [loadingAvatar, setLoadingAvatar] = useState<boolean>(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadAvatarImage = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("avatar", file);
      userService
        .updateAvatarImage(formData)
        .then((result) => {
          setLoadingAvatar(false);
          dispatch(updateToDefaultAvatar(result));
          message.success(t("avatar-updated-success-message"));
        })
        .catch((error) => {
          message.error(t(error));
        });
    },
    [dispatch, t]
  );

  const removeAvatarImage = useCallback(async () => {
    setShowConfirmDeleteModal(false);
    userService
      .updateToDefaultAvatar()
      .then((result) => {
        dispatch(updateToDefaultAvatar(result));
        message.success(t("avatar-removed-success-message"));
      })
      .catch((error) => {
        message.error(t(error));
      });
  }, [dispatch, t]);

  const handleEditAvatar = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setShowOptionsModal(false);
      setLoadingAvatar(true);
      if (isImageAcceptedFormat(file.type)) {
        uploadAvatarImage(file);
        return;
      }

      try {
        if (file.type === "image/heic" || file.type === "") {
          const convertedFile = await convertHeicFile(file as RcFile);
          uploadAvatarImage(convertedFile);
        } else {
          message.error(t("unsupported-file-format-error-message"));
          setLoadingAvatar(false);
        }
      } catch (error) {
        message.error(t("unsupported-file-format-error-message"));
        setLoadingAvatar(false);
      }
    }
    if (e.target) {
      e.target.value = "";
    }
  };

  const handleDeleteAvatar = () => {
    setShowOptionsModal(false);
    setShowConfirmDeleteModal(true);
  };

  const handleOpenOptionsModal = () => {
    setShowOptionsModal(true);
  };

  return (
    <Row className="w-full justify-content-between mt-20">
      <Col className="vertical-center pl-11">
        <img src={EditCover1} width={90} alt="" />
      </Col>
      <Col className="editProfileAvatarWrapper mt-20">
        <div className="editProfileAvatarUploadContent">
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} disabled={loadingAvatar} />
          <img src={EditIcon} width={28} alt="icon" onClick={handleOpenOptionsModal} />
        </div>
        <div className="editProfileAvatarContent">
          <div
            className="editProfileAvatarImage"
            style={{
              backgroundImage: `url("${user?.avatar}")`,
            }}
          >
            {loadingAvatar ? <LoadingOutlined style={{ fontSize: "32px" }} /> : null}
          </div>
        </div>
      </Col>
      <Col className="vertical-center">
        <img src={EditCover2} width={95} alt="" />
      </Col>

      {showOptionsModal && (
        <AvatarOptionsModal
          onClose={() => setShowOptionsModal(false)}
          onEdit={handleEditAvatar}
          onDelete={handleDeleteAvatar}
          isDeleteDisabled={user?.avatar?.includes("default.jpg") || false}
        />
      )}

      {showConfirmDeleteModal && <ConfirmDeleteAvatarModal onConfirm={removeAvatarImage} onCancel={() => setShowConfirmDeleteModal(false)} />}
    </Row>
  );
}
